import React, { Fragment, useState, useLayoutEffect, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { v4 as uuidv4 } from 'uuid'
import { uploadData, getUrl, downloadData } from 'aws-amplify/storage'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import Dropdown from '../../common/Dropdown'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ToggleSwitch from '../../common/ToggleSwitch'
import { toast } from '../../common/Toast'
import CheckboxInput from '../../common/CheckboxInput'
import ExpandableSection from '../../common/ExpandableSection'
import RadioInput from '../../common/RadioInput'
import SimpleTextInput from '../../common/SimpleTextInput'
import ConfirmationModal from '../../common/ConfirmationModal'
import WrappedTabs from '../../common/WrappedTabs'
import TooltipedIcon from '../../common/TooltipedIcon'
import { TrashCanFull, UploadCloudFull, UrlIcon, CloseCircleIcon, CalendarIcon, InformationIcon } from '../../../icons'
import { capitalizeFirstLetter, capitalizeWords, getFileIcon } from '../../../utils/functions'
import { languagesShort } from '../../../utils/languages'
import { documentUploadDeletePermission, documentUploadWritePermission } from '../../../utils/permissionValidation'
import countries from '../../../utils/countries.json'
import { createDocument, removeDocumentFile, updateDocument, updateDocumentExternalFile, updateDocumentFile } from '../../../actions/documents'
import { useWindowSize } from '../../../libs/hooks'
import { getAvailableGroup } from '../../../utils/userGroups'
import '../uploads.scss'

const AddEditDocumentModal = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { selectedDocument, closeModal, showSpinner, setShowSpinner } = props
  const [title, setTitle] = useState('')
  const [accessLevel, setAccessLevel] = useState('')
  const [documentType, setDocumentType] = useState('')
  const [documentCategory, setDocumentCategory] = useState('')
  const [isExternalDocument, setIsExternalDocument] = useState(false)
  const [externalFiles, setExternalFiles] = useState([])
  const [files, setFiles] = useState([])
  const [selectedModels, setSelectedModels] = useState([])
  const [deviceCategoriesWithModels, setDeviceCategoriesWithModels] = useState([])
  const [selectedDeviceCategory, setSelectedDeviceCategory] = useState(null)
  const [expandedItem, setExpandedItem] = useState(null)
  const [fileIdToDelete, setFileIdToDelete] = useState(null)
  const [showChangeExternalConfirmModal, setShowChangeExternalConfirmModal] = useState(false)
  const [showDeleteFileConfirmModal, setShowDeleteFileConfirmModal] = useState(false)
  const [haveError, setHaveError] = useState({})
  const documentStatuses = ['inactive', 'active']
  const modelsWrappedTabsId = 'models-wrapped-tabs'
  const tsbCategory = 'Technical Service Bulletins'

  useLayoutEffect(() => {
    const groupedModels = props.adminDocumentModels.reduce((acc, model) => {
      if (!acc[model.deviceCategory]) {
        acc[model.deviceCategory] = []
      }

      acc[model.deviceCategory].push(model)
      return acc
    }, {})

    const sortedDeviceCategories = Object.keys(groupedModels).sort()

    const sortedDeviceCategoriesWithModels = sortedDeviceCategories.map((deviceCategory) => ({
      deviceCategory,
      models: groupedModels[deviceCategory].sort((a, b) => a.modelName.localeCompare(b.modelName)),
    }))

    setDeviceCategoriesWithModels(sortedDeviceCategoriesWithModels)
    setSelectedDeviceCategory(sortedDeviceCategoriesWithModels[0]?.deviceCategory)
  }, [props.adminDocumentModels])

  useLayoutEffect(() => {
    if (selectedDocument) {
      setTitle(selectedDocument.title)
      setAccessLevel(selectedDocument.accessLevel)
      setDocumentType(selectedDocument.documentType)
      setDocumentCategory(selectedDocument.documentCategory)
      setIsExternalDocument(selectedDocument.isExternalDocument)
      setExternalFiles(selectedDocument.externalDocuments || [])
      setFiles(selectedDocument.files || [])
      setSelectedModels(selectedDocument.models || [])
    }
  }, [selectedDocument])

  useEffect(() => {
    if (!selectedDocument && isExternalDocument && externalFiles.length === 0) {
      setExternalFiles([
        {
          id: uuidv4(),
          url: '',
          isActive: false,
          languageCode: 'ML',
          multiLanguageCodes: [],
          isNew: true,
        },
      ])
    }

    if (isExternalDocument && documentCategory === tsbCategory) {
      setDocumentCategory('')
    }

    if (isExternalDocument) {
      setAccessLevel('public')
    }
  }, [isExternalDocument])

  useEffect(() => {
    if (!selectedDocument && externalFiles.length === 0) {
      setIsExternalDocument(false)
    }
  }, [externalFiles])

  useEffect(() => {
    if (documentCategory === tsbCategory) {
      setAccessLevel('protected')
    }
  }, [documentCategory])

  useEffect(() => {
    if (documentType === 'Technical Service Bulletin') {
      setDocumentCategory(tsbCategory)
    }
  }, [documentType])

  const documentFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={() => closeModal()}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton
          className="submit-button"
          onClick={handleAddEditUploadDocument}
          disabled={showSpinner || hasSameInput()}
          requiredPermission={documentUploadWritePermission}
        >
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const CustomDateInput = React.forwardRef(({ value, onClick, fileId }, ref) => (
    <div className={'custom-date-input' + (haveError[`publishDate-${fileId}`] ? ' has-error' : '')} ref={ref} onClick={onClick}>
      <div className={'date-text' + (!value ? ' placeholder-text' : '')}>{value ? value : ''}</div>
      {value && (
        <button
          className="reset-date-button"
          onClick={(e) => {
            e.stopPropagation()
            updateFile(fileId, { publishDate: null })
          }}
        >
          <CloseCircleIcon />
        </button>
      )}
      <CalendarIcon className="calendar-icon" />
    </div>
  ))

  const hasSameInput = () => {
    return (
      selectedDocument &&
      title === selectedDocument.title &&
      accessLevel === selectedDocument.accessLevel &&
      documentType === selectedDocument.documentType &&
      documentCategory === selectedDocument.documentCategory &&
      selectedModels.every((model) => selectedDocument.models?.includes(model)) &&
      selectedModels.length === selectedDocument.models.length &&
      ((isExternalDocument && JSON.stringify(externalFiles) === JSON.stringify(selectedDocument.externalDocuments)) ||
        (!isExternalDocument && JSON.stringify(files) === JSON.stringify(selectedDocument.files)))
    )
  }

  const selectAccessLevel = (accessLevel) => {
    setAccessLevel(accessLevel)
  }

  const selectDocumentType = (docType) => {
    setDocumentType(docType)
  }

  const selectDocumentCategory = (category) => {
    setDocumentCategory(category)
  }

  const handleChangeExternal = () => {
    if (isExternalDocument) {
      setExternalFiles([])
    } else {
      setFiles([])
    }

    setIsExternalDocument((prevValue) => !prevValue)
    setShowChangeExternalConfirmModal(false)
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!title.trim()) {
      errors = { ...errors, title: true }
      isValid = false
    }

    if (!accessLevel) {
      errors = { ...errors, accessLevel: true }
      isValid = false
    }

    if (!documentType) {
      errors = { ...errors, docType: true }
      isValid = false
    }

    if (!documentCategory) {
      errors = { ...errors, category: true }
      isValid = false
    }

    if (isExternalDocument) {
      externalFiles.forEach((externalFile) => {
        if (!externalFile.url.trim()) {
          errors = { ...errors, [`externalUrl-${externalFile.id}`]: true }
          isValid = false
        }
      })

      externalFiles.forEach((externalFile) => {
        if (
          !externalFile.languageCode ||
          (externalFile.languageCode === 'ML' && (!externalFile.multiLanguageCodes || externalFile.multiLanguageCodes.length === 0))
        ) {
          errors = { ...errors, [`languages-${externalFile.id}`]: true }
          isValid = false
        }
      })
    }

    if (files.length > 0) {
      if (documentCategory === tsbCategory) {
        files.forEach((file) => {
          if (!file.publishDate) {
            errors = { ...errors, [`publishDate-${file.id}`]: true }
            isValid = false
          }
        })
      }

      files.forEach((file) => {
        if (!file.regions || file.regions.length === 0 || file.regions.every((region) => !region.countries || region.countries.length === 0)) {
          errors = { ...errors, [`regions-${file.id}`]: true }
          isValid = false
        }
      })

      files.forEach((file) => {
        if (!file.languageCode || (file.languageCode === 'ML' && (!file.multiLanguageCodes || file.multiLanguageCodes.length === 0))) {
          errors = { ...errors, [`languages-${file.id}`]: true }
          isValid = false
        }
      })
    }

    setHaveError(errors)
    return isValid
  }

  const handleMoveFilesToS3 = async (s3FilesPath) => {
    try {
      setShowSpinner(true)
      for (const file of files) {
        const fileName = file.isNew ? file.fileContent?.name : file.fileName

        if (!fileName) continue
        const destKey = `${s3FilesPath}${fileName}`.replace(/^\/+/, '')

        if (file.isNew) {
          await uploadData({
            path: destKey,
            data: file.fileContent,
            options: {
              contentType: file.fileContent?.type,
            },
          }).result
        } else {
          const currentFilePath = file.filePath || ''
          const srcKey = `${currentFilePath}${fileName}`.replace(/^\/+/, '')

          if (s3FilesPath !== currentFilePath) {
            const { url } = await getUrl({
              path: srcKey,
            })

            const res = await fetch(url.toString())
            if (!res.ok) throw new Error(`Failed to fetch S3 object: ${srcKey}`)
            const blob = await res.blob()

            await uploadData({
              path: destKey,
              data: blob,
              options: {
                contentType: blob.type || file.fileType,
              },
            }).result
          }
        }
      }
      setShowSpinner(false)
      return true
    } catch (error) {
      console.log('Error while uploading file to S3', error)
      setShowSpinner(false)
      return false
    }
  }

  const buildS3FilePath = () => {
    let path = 'documents/'

    path += `${accessLevel}/`

    if (documentCategory === tsbCategory) {
      const currentYear = new Date().getFullYear()
      path += `technical-service-bulletins/${currentYear}/`

      return path
    }

    if (selectedModels.length > 0) {
      const usedDeviceCategories = props.adminDocumentModels.filter((model) => selectedModels.includes(model.modelName)).map((model) => model.deviceCategory)
      const usedDeviceProductType = props.adminDocumentModels.filter((model) => selectedModels.includes(model.modelName)).map((model) => model.productType)
      const uniqueDeviceCategories = new Set(usedDeviceCategories)
      const uniqueDeviceProductType = new Set(usedDeviceProductType)

      if (uniqueDeviceCategories.size === 1) {
        path += `${[...uniqueDeviceCategories][0]?.toLowerCase()?.replace(' ', '-')}/`
      }

      if (uniqueDeviceProductType.size === 1) {
        path += `${[...usedDeviceProductType][0]?.toLowerCase()?.replace(' ', '-')}/`
      }
    }

    return path
  }

  const handleAddEditUploadDocument = async () => {
    if (!isExternalDocument && files.length === 0) {
      toast.error(t('error.must_add_one_file'))
      return
    }

    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    let s3FilesPath = ''

    if (!isExternalDocument) {
      s3FilesPath = buildS3FilePath()
    }

    const bodyParams = {
      title: title.trim(),
      accessLevelId: props.accessLevels.find((accessLevelOption) => accessLevelOption.name === accessLevel)?.id,
      documentTypeId: props.documentTypes.find((typeOption) => typeOption.name === documentType)?.id,
      documentCategoryId: props.documentCategories.find((categoryOption) => categoryOption.value === documentCategory)?.id,
      isActive: (isExternalDocument ? externalFiles : files).some((file) => file.isActive),
      isExternalDocument: isExternalDocument,
      files: files.map((file) => ({
        ...(!file.isNew && { id: file.id }),
        fileName: file.isNew ? file.fileContent?.name : file.fileName,
        filePath: s3FilesPath,
        fileType: file.isNew ? file.fileContent?.name?.split('.')?.pop() : file.fileType,
        fileTitle: `${title.trim()}${file.revision?.length > 0 ? ` R${file.revision}` : ''} (${file.languageCode !== 'ML' ? file.languageCode : file.multiLanguageCodes.join('/')})`,
        ...(file.revision?.length > 0 && { revision: file.revision }),
        ...(file.documentNumber?.length > 0 && { documentNumber: file.documentNumber }),
        isActive: file.isActive,
        regions: file.regions.map((region) => ({
          id: props.regions.find((r) => r.name === region.name)?.id,
          countries: props.regions.find((r) => r.name === region.name)?.countries?.length === region.countries?.length ? ['ALL'] : region.countries,
        })),
        languageCode: file.languageCode,
        multiLanguageCodes: file.multiLanguageCodes,
        publishDate: file.publishDate,
      })),
      externalDocuments: externalFiles.map((file) => ({
        ...(!file.isNew && { id: file.id }),
        fileTitle: title.trim(),
        url: file.url.startsWith('http') ? file.url : `https://${file.url}`,
        languageCode: file.languageCode,
        multiLanguageCodes: file.multiLanguageCodes,
        isActive: file.isActive,
      })),
      models: selectedModels,
    }

    if (!isExternalDocument) {
      const isSuccesfull = await handleMoveFilesToS3(s3FilesPath)

      if (!isSuccesfull) {
        toast.error(t('error.something_wrong'))
        return
      }
    }

    if (selectedDocument) {
      props.actions.updateDocument(selectedDocument.id, bodyParams).then(() => {
        closeModal()
      })
      return
    }

    props.actions.createDocument(bodyParams).then(() => {
      closeModal()
    })
  }

  const addFiles = async (event) => {
    setFiles([
      ...files,
      ...Array.from(event.target.files).map((file) => ({
        id: uuidv4(),
        fileContent: file,
        fileName: file.name,
        revision: '',
        documentNumber: '',
        publishDate: null,
        isActive: false,
        regions: [],
        languageCode: 'ML',
        multiLanguageCodes: [],
        isNew: true,
      })),
    ])

    if (event.target) {
      event.target.value = ''
    }
  }

  const handleDrop = async (event) => {
    event.preventDefault()
    const { files } = event.dataTransfer

    if (files && files.length) {
      setFiles((prevSelectedFiles) => [
        ...prevSelectedFiles,
        ...Array.from(files).map((file) => ({
          id: uuidv4(),
          fileContent: file,
          fileName: file.name,
          revision: '',
          documentNumber: '',
          publishDate: null,
          isActive: false,
          regions: [],
          languageCode: 'ML',
          multiLanguageCodes: [],
          isNew: true,
        })),
      ])
    }

    if (event.target) {
      event.target.value = ''
    }
  }

  const updateExternalFile = (id, updatedFields) => {
    const updatedExternalFiles = externalFiles.map((externalFile) => (externalFile.id === id ? { ...externalFile, ...updatedFields } : externalFile))
    setExternalFiles(updatedExternalFiles)
  }

  const updateFile = (id, updatedFields) => {
    const updatedFiles = files.map((file) => (file.id === id ? { ...file, ...updatedFields } : file))
    setFiles(updatedFiles)
  }

  const toggleExpand = (id, index) => {
    setExpandedItem(expandedItem === `${id}-${index}` ? null : `${id}-${index}`)
  }

  const showDeleteConfirmationModal = (id) => {
    setFileIdToDelete(id)
    setShowDeleteFileConfirmModal(true)
  }

  const handleDeleteFile = () => {
    setShowDeleteFileConfirmModal(false)

    props.actions.removeDocumentFile(selectedDocument.id, fileIdToDelete)
  }

  const handleFileStatusUpdate = (fileId) => {
    const updatedFile = selectedDocument.files.find((file) => file.id === fileId)

    if (updatedFile) {
      props.actions.updateDocumentFile(selectedDocument.id, updatedFile.id, {
        ...updatedFile,
        documentNumber: updatedFile.documentNumber || undefined,
        revision: updatedFile.revision || undefined,
        isActive: !updatedFile.isActive,
        regions: updatedFile.regions?.map((r) => ({
          id: props.regions.find((region) => region.name === r.name)?.id,
          name: r.name,
          countries: props.regions.find((region) => region.name === r.name)?.countries?.length === r.countries?.length ? ['ALL'] : r.countries,
        })),
      })
    }
  }

  const handleExternalFileStatusUpdate = (fileId) => {
    const updatedFile = selectedDocument.externalDocuments.find((file) => file.id === fileId)

    if (updatedFile) {
      props.actions.updateDocumentExternalFile(selectedDocument.id, updatedFile.id, { ...updatedFile, isActive: !updatedFile.isActive })
    }
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal"
        title={selectedDocument ? t('document_context.edit_document') : t('document_context.add_new_document')}
        footerContent={documentFooterContent()}
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('title')}*`}</p>
              <SimpleTextInput value={title} hasError={haveError['title']} onChange={(value) => setTitle(value)} onClear={() => setTitle('')} />
              <div className="small-disclaimer">{t('document_context.document_upload_title_disclaimer')}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('access_level')}*`}</p>
              <Dropdown
                placeholder={t('please_select')}
                options={props.accessLevels.map((accessLevel) => ({ key: accessLevel.name, value: capitalizeFirstLetter(accessLevel.name) }))}
                initialSelectedKey={accessLevel}
                onChange={(accessLevel) => selectAccessLevel(accessLevel.key)}
                hasError={haveError['accessLevel']}
                isDisabled={documentCategory === tsbCategory || isExternalDocument}
              />
              {accessLevel && <div className="small-disclaimer">{`${t('available_to')}: ${getAvailableGroup(accessLevel)}`}</div>}
            </div>
            <div className="input-wrapper">
              <p className="input-option">{`${t('document_context.document_type')}*`}</p>
              <Dropdown
                placeholder={t('please_select')}
                options={props.documentTypes.map((typeOption) => ({ key: typeOption.name, value: typeOption.name }))}
                initialSelectedKey={documentType}
                onChange={(docType) => selectDocumentType(docType.key)}
                hasError={haveError['docType']}
              />
            </div>
            <div className="input-wrapper">
              <p className="input-option">{`${t('category')}*`}</p>
              <Dropdown
                placeholder={t('please_select')}
                options={props.documentCategories
                  .map((category) => category.value)
                  .filter((category) => !isExternalDocument || category !== tsbCategory)
                  .map((categoryOption) => ({ key: categoryOption, value: categoryOption }))}
                initialSelectedKey={documentCategory}
                onChange={(category) => selectDocumentCategory(category.key)}
                hasError={haveError['category']}
              />
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">
            {!selectedDocument || !selectedDocument.isExternalDocument
              ? t('nav.documents').toUpperCase()
              : t('document_context.external_documents').toUpperCase()}
          </div>
          {!selectedDocument && (
            <div className="row-wrapper">
              <div className="input-wrapper flex-row">
                <CheckboxInput
                  checked={isExternalDocument}
                  text={t('document_context.external_documents')}
                  onChange={() => {
                    if (isExternalDocument && externalFiles.length > 0 && externalFiles.some((file) => file.url.trim().length > 0)) {
                      setShowChangeExternalConfirmModal(true)
                      return
                    }

                    if (!isExternalDocument && files.length > 0) {
                      setShowChangeExternalConfirmModal(true)
                      return
                    }

                    handleChangeExternal()
                  }}
                />
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.external_document_text')}`} />
              </div>
            </div>
          )}
          {isExternalDocument && (
            <>
              {externalFiles.map((externalFile, index) => (
                <Fragment key={externalFile.id}>
                  {index !== 0 && <div className="inner-separator" />}
                  <div className={'row-wrapper' + (index === 0 ? ' reduced-margin-top' : '')}>
                    <div className="input-wrapper">
                      <p className="input-option">
                        {`${t('url')}*`}
                        <div className="d-flex flex-align-center external-doc">
                          <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.toggle_text')}`} />
                          <div
                            className="d-flex flex-row align-center without-width with-auto-margin-left"
                            onClick={() =>
                              externalFile.isNew
                                ? updateExternalFile(externalFile.id, { isActive: !externalFile.isActive })
                                : handleExternalFileStatusUpdate(externalFile.id)
                            }
                          >
                            <ToggleSwitch checked={externalFile.isActive} />
                            <div className="without-font-weight">{t(documentStatuses[Number(externalFile.isActive)])}</div>
                          </div>
                          {!selectedDocument && (
                            <TrashCanFull
                              className="action-icon with-margin-left"
                              onClick={() => setExternalFiles(externalFiles.filter((file) => file.id !== externalFile.id))}
                            />
                          )}
                        </div>
                      </p>
                      <SimpleTextInput
                        icon={<UrlIcon />}
                        value={externalFile.url}
                        placeholder={t('document_context.please_insert_document_url')}
                        hasError={haveError[`externalUrl-${externalFile.id}`]}
                        onChange={(url) => updateExternalFile(externalFile.id, { url })}
                        onClear={() => updateExternalFile(externalFile.id, { url: '' })}
                      />
                    </div>
                  </div>
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <p className="input-option">{`${t('languages')}*`}</p>
                      {haveError[`languages-${externalFile.id}`] && <span className="error-message">{t('error.must_select_one_language')}</span>}
                      <CheckboxInput
                        checked={externalFile.languageCode === 'ML'}
                        text={t('multilanguage')}
                        onChange={() =>
                          updateExternalFile(externalFile.id, { languageCode: externalFile.languageCode === 'ML' ? null : 'ML', multiLanguageCodes: [] })
                        }
                      />
                    </div>
                  </div>
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <div className="ordered-list-wrapper">
                        <div className="columns-wrapper">
                          {props.documentLanguages.map((language, index) => (
                            <div className="item-wrapper" key={index}>
                              {externalFile.languageCode === 'ML' ? (
                                <CheckboxInput
                                  checked={externalFile.multiLanguageCodes?.includes(language)}
                                  text={`${t(languagesShort[language.toLowerCase()] || language)} (${language})`}
                                  onChange={() =>
                                    updateExternalFile(externalFile.id, {
                                      multiLanguageCodes: externalFile.multiLanguageCodes.includes(language)
                                        ? externalFile.multiLanguageCodes.filter((l) => l !== language)
                                        : [...externalFile.multiLanguageCodes, language],
                                    })
                                  }
                                />
                              ) : (
                                <RadioInput
                                  checked={externalFile.languageCode === language}
                                  text={`${t(languagesShort[language.toLowerCase()] || language)} (${language})`}
                                  onChange={() => updateExternalFile(externalFile.id, { languageCode: language })}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Fragment>
              ))}
            </>
          )}
          {!isExternalDocument && (
            <>
              {files.map((file, index) => (
                <Fragment key={index}>
                  {index !== 0 && <div className="inner-separator" />}
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <p className="input-option">
                        {t('file_name')}
                        {isMobile && (
                          <div className="action d-flex flex-row without-font-weight align-center with-auto-margin-left">
                            <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.toggle_text')}`} />
                            <div
                              className="d-flex flex-row align-center"
                              onClick={() => (file.isNew ? updateFile(file.id, { isActive: !file.isActive }) : handleFileStatusUpdate(file.id))}
                            >
                              <ToggleSwitch checked={file.isActive} />
                              <div className="without-font-weight">{t(documentStatuses[Number(file.isActive)])}</div>
                            </div>
                            {(files.length > 1 || file.isNew) && (
                              <TrashCanFull
                                className={`action action-icon ${!file.isNew && file.isActive ? 'disabled-action' : ''}`}
                                onClick={() => (file.isNew ? setFiles(files.filter((f) => f.id !== file.id)) : showDeleteConfirmationModal(file.id))}
                              />
                            )}
                          </div>
                        )}
                      </p>
                      <div className="value with-min-height">
                        {getFileIcon(file.fileName)}
                        {file.fileName}
                      </div>
                    </div>
                    {!isMobile && (
                      <div className="input-wrapper max-content-width d-flex flex-row align-center with-auto-margin-left">
                        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.toggle_text')}`} />
                        <div
                          className="d-flex flex-row with-auto-margin-left with-margin-right"
                          onClick={() => (file.isNew ? updateFile(file.id, { isActive: !file.isActive }) : handleFileStatusUpdate(file.id))}
                        >
                          <ToggleSwitch checked={file.isActive} />
                          <div className="status">{t(documentStatuses[Number(file.isActive)])}</div>
                        </div>
                        {(files.length > 1 || file.isNew) && (
                          <TrashCanFull
                            className={`action action-icon ${!file.isNew && file.isActive ? 'disabled-action' : ''}`}
                            onClick={() => (file.isNew ? setFiles(files.filter((f) => f.id !== file.id)) : showDeleteConfirmationModal(file.id))}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  {title.trim().length > 0 && (
                    <div className="row-wrapper">
                      <div className="input-wrapper">
                        <p className="input-option">{t('file_title')}</p>
                        <div className="value">{`${title.trim()}${file.revision?.length > 0 ? ` R${file.revision}` : ''} (${file.languageCode !== 'ML' ? file.languageCode : file.multiLanguageCodes?.join('/')})`}</div>
                      </div>
                    </div>
                  )}
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <p className="input-option">{`${t('document_context.document_number')}`}</p>
                      <input
                        type="text"
                        className={'name-input'}
                        value={file.documentNumber}
                        onChange={(e) => updateFile(file.id, { documentNumber: e.target.value })}
                      />
                    </div>
                    <div className="input-wrapper">
                      <p className="input-option">{`${t('document_context.revision_number')}`}</p>
                      <input type="text" className={'name-input'} value={file.revision} onChange={(e) => updateFile(file.id, { revision: e.target.value })} />
                    </div>
                    <div className="input-wrapper">
                      <p className="input-option">{`${t('publish_date')}${documentCategory === tsbCategory ? '*' : ''}`}</p>
                      <DatePicker
                        selected={file.publishDate ? new Date(file.publishDate) : ''}
                        onChange={(date) => {
                          updateFile(file.id, { publishDate: new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString() })
                        }}
                        dateFormat={'yyyy-MM-dd'}
                        yearItemNumber={9}
                        customInput={<CustomDateInput fileId={file.id} />}
                        showYearDropdown
                        showMonthDropdown
                      />
                    </div>
                  </div>
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <p className="input-option">{`${t('regions')}*`}</p>
                      {haveError[`regions-${file.id}`] && <span className="error-message">{t('error.must_select_one_region')}</span>}
                      <div className="ordered-list-wrapper">
                        <div className="columns-wrapper">
                          {props.regions.map((region, index) => (
                            <div className="item-wrapper" key={index}>
                              <CheckboxInput
                                checked={file.regions?.map((r) => r.name).includes(region.name)}
                                text={region.name}
                                onChange={() =>
                                  updateFile(file.id, {
                                    regions: file.regions?.map((r) => r.name).includes(region.name)
                                      ? file.regions.filter((r) => r.name !== region.name)
                                      : [...(file.regions || []), JSON.parse(JSON.stringify(region))],
                                  })
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  {false &&
                    file.regions
                      ?.sort((a, b) => a.name?.localeCompare(b.name))
                      ?.map((region, index) => (
                        <div className="row-wrapper" key={`countries-${index}`}>
                          <ExpandableSection
                            title={`${region.name} ${t('countries')}`}
                            isExpanded={expandedItem === `${file.id}-${index}`}
                            onToggle={() => toggleExpand(file.id, index)}
                            className="internal-expandable"
                            additionalContent={
                              expandedItem === `${file.id}-${index}` ? (
                                <div className="expandable-action">
                                  <span
                                    className={
                                      props.regions.find((r) => r.name === region.name)?.countries?.length !== region.countries?.length ? 'available' : ''
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      updateFile(file.id, {
                                        regions: file.regions.map((r) =>
                                          r.name === region.name
                                            ? {
                                                ...r,
                                                countries: props.regions.find((reg) => reg.name === region.name)?.countries || [],
                                              }
                                            : r,
                                        ),
                                      })
                                    }}
                                  >
                                    {t('select_all')}
                                  </span>
                                  <span>|</span>
                                  <span
                                    className={region.countries?.length > 0 ? 'available' : ''}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      updateFile(file.id, {
                                        regions: file.regions.map((r) =>
                                          r.name === region.name
                                            ? {
                                                ...r,
                                                countries: [],
                                              }
                                            : r,
                                        ),
                                      })
                                    }}
                                  >
                                    {capitalizeWords(t('clear_all'))}
                                  </span>
                                </div>
                              ) : (
                                <div className="counter-badge">
                                  {region.countries?.includes('ALL')
                                    ? props.regions.find((r) => r.name === region.name)?.countries?.length
                                    : region.countries?.length || 0}
                                </div>
                              )
                            }
                          >
                            <div className="ordered-list-wrapper">
                              <div className="columns-wrapper">
                                {props.regions
                                  .find((r) => r.name === region.name)
                                  ?.countries?.map((country) => (
                                    <div className="item-wrapper" key={country}>
                                      <CheckboxInput
                                        checked={region.countries?.includes(country) || region.countries?.includes('ALL')}
                                        text={countries.find((c) => c.code === country)?.name || ''}
                                        onChange={() =>
                                          updateFile(file.id, {
                                            regions: file.regions.map((r) =>
                                              r.name === region.name
                                                ? {
                                                    ...r,
                                                    countries: r.countries.includes(country)
                                                      ? r.countries.filter((c) => c !== country)
                                                      : [...r.countries, country],
                                                  }
                                                : r,
                                            ),
                                          })
                                        }
                                      />
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </ExpandableSection>
                        </div>
                      ))}
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <p className="input-option">{`${t('languages')}*`}</p>
                      {haveError[`languages-${file.id}`] && <span className="error-message">{t('error.must_select_one_language')}</span>}
                      <CheckboxInput
                        checked={file.languageCode === 'ML'}
                        text={t('multilanguage')}
                        onChange={() => updateFile(file.id, { languageCode: file.languageCode === 'ML' ? null : 'ML', multiLanguageCodes: [] })}
                      />
                    </div>
                  </div>
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <div className="ordered-list-wrapper">
                        <div className="columns-wrapper">
                          {props.documentLanguages.map((language, index) => (
                            <div className="item-wrapper" key={index}>
                              {file.languageCode === 'ML' ? (
                                <CheckboxInput
                                  checked={file.multiLanguageCodes?.includes(language)}
                                  text={`${t(languagesShort[language.toLowerCase()] || language)} (${language})`}
                                  onChange={() =>
                                    updateFile(file.id, {
                                      multiLanguageCodes: file.multiLanguageCodes.includes(language)
                                        ? file.multiLanguageCodes.filter((l) => l !== language)
                                        : [...file.multiLanguageCodes, language],
                                    })
                                  }
                                />
                              ) : (
                                <RadioInput
                                  checked={file.languageCode === language}
                                  text={`${t(languagesShort[language.toLowerCase()] || language)} (${language})`}
                                  onChange={() => updateFile(file.id, { languageCode: language })}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Fragment>
              ))}
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option flex-justify-start">
                    {`${t('document_context.upload_document')}*`}
                    <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.upload_document_text')}`} />
                  </p>
                  <div className="upload-wrapper" onDrop={(e) => handleDrop(e)} onDragOver={(e) => e.preventDefault()}>
                    <label className="file-input-wrapper" htmlFor="file-input">
                      <UploadCloudFull />
                      <div>
                        <span>{t('other_context.drop_files_to_attach_or')}</span>
                        <span className="call-to-action">{` ${t('browse')}`}</span>
                      </div>
                    </label>
                    <input className="file-input" id="file-input" onChange={(e) => addFiles(e)} type="file" multiple />
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="separator" />
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('monitoring.models')}</p>
              <WrappedTabs
                containerId={modelsWrappedTabsId}
                tabs={deviceCategoriesWithModels.map((deviceCategory) => ({
                  id: deviceCategory.deviceCategory,
                  name: deviceCategory.deviceCategory,
                  ...(deviceCategory.models?.filter((m) => selectedModels.includes(m.modelName))?.length > 0 && {
                    additionalContent: (
                      <div className="counter-badge small-badge">{deviceCategory.models?.filter((m) => selectedModels.includes(m.modelName))?.length}</div>
                    ),
                  }),
                }))}
                activeTab={selectedDeviceCategory}
                changeTab={(deviceCategory) => setSelectedDeviceCategory(deviceCategory)}
              />
              {selectedDeviceCategory && (
                <div className="ordered-list-wrapper">
                  <div className="columns-wrapper">
                    {deviceCategoriesWithModels
                      .find((deviceCategory) => deviceCategory.deviceCategory === selectedDeviceCategory)
                      ?.models.map((model, index) => (
                        <div className="item-wrapper" key={index}>
                          <CheckboxInput
                            checked={selectedModels.includes(model.modelName)}
                            text={model.modelName}
                            onChange={() =>
                              setSelectedModels((prevValue) =>
                                prevValue.includes(model.modelName) ? prevValue.filter((value) => value !== model.modelName) : [...prevValue, model.modelName],
                              )
                            }
                          />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </PageModal>
      {showChangeExternalConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowChangeExternalConfirmModal(false)}
          onAccept={() => handleChangeExternal()}
          onCancel={() => setShowChangeExternalConfirmModal(false)}
          message={t('document_context.confirm_change_external')}
        />
      )}
      {showDeleteFileConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteFileConfirmModal(false)}
          onAccept={() => handleDeleteFile()}
          onCancel={() => setShowDeleteFileConfirmModal(false)}
          message={t('confirm_delete_file')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={documentUploadDeletePermission}
        />
      )}
    </>
  )
}

function stateToProps({ documents, shared }) {
  return {
    accessLevels: shared?.accessLevels || [],
    documentTypes: documents?.documentTypes || [],
    documentCategories: documents?.documentCategories || [],
    documentLanguages: shared?.languages?.map((l) => l.languageCode) || [],
    regions: shared?.regions || [],
    adminDocumentModels: documents?.adminDocumentModels || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createDocument,
        updateDocument,
        removeDocumentFile,
        updateDocumentFile,
        updateDocumentExternalFile,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditDocumentModal)

